/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import { Buffer } from 'node:buffer';

/**
 * Hash a userId to create a safe database name component.
 * This ensures user data isolation by creating unique database names per user.
 *
 * @param userId - The user ID to hash (e.g., "@user:example.com")
 * @returns A safe string suitable for use in database names
 *
 * @example
 * ```typescript
 * const hash = hashUserId('@alice:matrix.org');
 * // Returns: "QGFsaWNlOm1hdHJpeC5vcmc"
 * ```
 */
export function hashUserId(userId: string): string {
  if (!userId) {
    return 'anonymous';
  }

  try {
    // Use base64 encoding for a deterministic, reversible hash
    // This allows debugging and ensures consistency across sessions
    let encoded: string;
    if (typeof btoa !== 'undefined') {
      encoded = btoa(userId);
    } else {
      // Node.js environment
      encoded = Buffer.from(userId).toString('base64');
    }

    // Replace characters that might cause issues in database names
    // IndexedDB names should be safe with alphanumeric and basic punctuation
    return encoded.replace(/[+/=]/g, (char) => {
      switch (char) {
        case '+':
          return '-';
        case '/':
          return '_';
        case '=':
          return '';
        default:
          return char;
      }
    });
  } catch (error) {
    // Fallback to a simple sanitization if base64 fails
    console.warn('Failed to encode userId, using fallback hash', error);
    return userId.replace(/[\W_]/g, '_').substring(0, 50);
  }
}

/**
 * Decode a hashed userId back to its original form.
 * Useful for debugging and logging purposes.
 *
 * @param hash - The hashed userId string
 * @returns The original userId, or null if decoding fails
 *
 * @example
 * ```typescript
 * const original = decodeUserIdHash('QGFsaWNlOm1hdHJpeC5vcmc');
 * // Returns: "@alice:matrix.org"
 * ```
 */
export function decodeUserIdHash(hash: string): string | null {
  if (!hash || hash === 'anonymous') {
    return null;
  }

  try {
    // Reverse the character replacements
    const base64 = hash.replace(/[-_]/g, (char) => {
      switch (char) {
        case '-':
          return '+';
        case '_':
          return '/';
        default:
          return char;
      }
    });

    // Decode from base64
    if (typeof atob !== 'undefined') {
      return atob(base64);
    } else {
      // Node.js environment
      return Buffer.from(base64, 'base64').toString('utf-8');
    }
  } catch (error) {
    console.warn('Failed to decode userId hash', error);
    return null;
  }
}

/**
 * Validate if a userId hash matches the expected userId.
 * Used for security checks when loading data from storage.
 *
 * @param hash - The stored hash
 * @param userId - The current userId to validate against
 * @returns true if the hash matches the userId
 *
 * @example
 * ```typescript
 * const isValid = validateUserIdHash(storedHash, currentUserId);
 * if (!isValid) {
 *   // Data belongs to different user, clear it!
 * }
 * ```
 */
export function validateUserIdHash(hash: string, userId: string): boolean {
  if (!hash || !userId) {
    return false;
  }

  const expectedHash = hashUserId(userId);
  return hash === expectedHash;
}
